import { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Speaker, CalendarPlus, ListMusic, Users, MessageSquare, BarChart3, CheckCircle } from 'lucide-react';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
}

const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to VoteBeats!',
    description: 'Let us show you around. VoteBeats helps you manage song requests at your events with audience voting.',
    icon: Speaker,
  },
  {
    id: 'create-event',
    title: 'Create Your First Event',
    description: 'Start by creating an event. Each event gets a unique code that attendees can use to submit and vote on song requests.',
    icon: CalendarPlus,
  },
  {
    id: 'manage-queue',
    title: 'Manage Your Queue',
    description: 'View and manage all song requests in one place. Approve, reject, or mark songs as played with ease.',
    icon: ListMusic,
  },
  {
    id: 'audience-voting',
    title: 'Audience Voting',
    description: 'Your audience votes on their favorite songs. The most popular requests rise to the top of your queue.',
    icon: Users,
  },
  {
    id: 'messaging',
    title: 'Communicate with Attendees',
    description: 'Send announcements to your audience. Let them know about breaks, last calls, or thank them for coming!',
    icon: MessageSquare,
  },
  {
    id: 'analytics',
    title: 'Track Your Performance',
    description: 'View detailed analytics about requests, voting patterns, and audience engagement at each event.',
    icon: BarChart3,
  },
  {
    id: 'complete',
    title: "You're All Set!",
    description: "You're ready to start accepting song requests. Create your first event and share the code with your audience!",
    icon: CheckCircle,
  },
];

interface OnboardingTourProps {
  onComplete: () => void;
  onSkip: () => void;
}

export default function OnboardingTour({ onComplete, onSkip }: OnboardingTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Lock body scroll while tour is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const step = ONBOARDING_STEPS[currentStep];
  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;
  const isFirstStep = currentStep === 0;
  const Icon = step.icon;

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    onSkip();
  };

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleSkip} />

      {/* Modal */}
      <div
        className={`relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden transform transition-all duration-300 ${
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        {/* Skip button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label="Skip onboarding"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Progress bar */}
        <div className="h-1 bg-gray-200 dark:bg-gray-700">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${((currentStep + 1) / ONBOARDING_STEPS.length) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Icon */}
          <div className="w-16 h-16 mx-auto mb-6 bg-primary/10 rounded-2xl flex items-center justify-center">
            <Icon className="w-8 h-8 text-primary" />
          </div>

          {/* Title and description */}
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-3">
            {step.title}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-center mb-8">
            {step.description}
          </p>

          {/* Step indicators */}
          <div className="flex justify-center gap-2 mb-8">
            {ONBOARDING_STEPS.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentStep
                    ? 'w-6 bg-primary'
                    : index < currentStep
                    ? 'bg-primary/50'
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
                aria-label={`Go to step ${index + 1}`}
              />
            ))}
          </div>

          {/* Navigation buttons */}
          <div className="flex gap-3">
            {!isFirstStep && (
              <button
                onClick={handlePrevious}
                className="btn btn-secondary flex-1"
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="btn btn-primary flex-1"
            >
              {isLastStep ? (
                <>
                  Get Started
                  <CheckCircle className="w-5 h-5 ml-2" />
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-5 h-5 ml-1" />
                </>
              )}
            </button>
          </div>

          {/* Skip link */}
          {!isLastStep && (
            <button
              onClick={handleSkip}
              className="w-full mt-4 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              Skip tour
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
